import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Col,
  Button,
  List,
  Divider,
  message,
  Tooltip,
  Modal,
  Space,
} from "antd";
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { Api_VariavelGlobal } from "../global";
import { FaVolumeMute, FaVolumeUp } from "react-icons/fa";

const PedidosNovos = () => {
  const audioRef = useRef(null);
  const [pedidosNovos, setPedidosNovos] = useState([]);
  const [usuarios, setUsuarios] = useState({});
  const [loadingPedido, setLoadingPedido] = useState(null);
  const [somHabilitado, setSomHabilitado] = useState(false);

  const pedidosExistentesRef = useRef(new Set());
  const { confirm } = Modal;

  // --- Utils ---
  const getTipoPagamento = (codigo) => {
    switch (codigo) {
      case 1: return "Dinheiro";
      case 2: return "Cartão";
      case 3: return "Pix";
      case 4: return "Ifood";
      case 5: return "Pagamento Online";
      default: return "Indefinido";
    }
  };

  const formatarValor = (valor) => {
    const num = Number(valor);
    return isNaN(num) ? "R$ 0,00" : `R$ ${num.toFixed(2).replace(".", ",")}`;
  };

  const formatarData = (dataISO) => {
    if (!dataISO) return "";
    const cleaned = dataISO.replace("Z", "");
    const [data, hora] = cleaned.split("T");
    if (!data || !hora) return dataISO;
    const [ano, mes, dia] = data.split("-");
    const horaLimpa = hora.split(".")[0];
    return `${dia}/${mes}/${ano} ${horaLimpa}`;
  };

  // --- Som ---
  const tocarAlarme = () => {
    if (!somHabilitado) return;
    if (!audioRef.current) {
      audioRef.current = new Audio("/sons/alarme.mp3");
      audioRef.current.loop = true;
      audioRef.current
        .play()
        .catch((err) => console.warn("Erro ao tocar áudio:", err));
    }
  };

  const pararAlarme = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
  };

  // --- Fetch usuários ---
  const fetchUsuarios = useCallback(async () => {
    try {
      const response = await fetch(`${Api_VariavelGlobal}/api/users`);
      if (!response.ok) throw new Error("Erro ao buscar usuários");

      const users = await response.json();
      const usuariosMap = {};
      users.forEach((user) => {
        usuariosMap[user.ID_USUARIO] = user.LOGIN;
      });

      setUsuarios(usuariosMap);
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      message.error("Erro ao buscar usuários.",1);
    }
  }, []);

  // --- Fetch pedidos ---
  const fetchPedidosNovos = useCallback(async () => {
    try {
      const response = await fetch(
        `${Api_VariavelGlobal}/api/pedido/status/3`
      );

      if (response.status === 404) {
        setPedidosNovos([]);
        pedidosExistentesRef.current.clear();
        pararAlarme();
        return;
      }

      if (!response.ok) throw new Error("Erro ao buscar pedidos");

      const data = await response.json();
      const pedidosArray = Array.isArray(data) ? data : [];

      const pedidosFormatados = pedidosArray
        .sort((a, b) => new Date(b.DATA) - new Date(a.DATA))
        .map((pedido) => ({
          ...pedido,
          TOTAL: formatarValor(pedido.TOTAL),
          DATA: formatarData(pedido.DATA),
          PAGAMENTO: getTipoPagamento(Number(pedido.PAGAMENTO)),
        }));

      // Detecta IDs novos
      const idsNovos = pedidosFormatados
        .map((p) => p.ID_PEDIDO)
        .filter((id) => !pedidosExistentesRef.current.has(id));

      if (idsNovos.length > 0) tocarAlarme();

      pedidosExistentesRef.current = new Set(
        pedidosFormatados.map((p) => p.ID_PEDIDO)
      );

      setPedidosNovos(pedidosFormatados);
      if (pedidosFormatados.length === 0) pararAlarme();
    } catch (error) {
      setPedidosNovos([]);
      pararAlarme();
    }
  }, [somHabilitado]);

  useEffect(() => {
    fetchUsuarios();
    fetchPedidosNovos();
    const interval = setInterval(fetchPedidosNovos, 2000);
    return () => clearInterval(interval);
  }, [fetchUsuarios, fetchPedidosNovos]);

  // --- Alterar status ---
  const alterarStatusPedido = async (ID_PEDIDO, STATUS, DATA) => {
    try {
      const response = await fetch(
        `${Api_VariavelGlobal}/api/pedido/${ID_PEDIDO}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ STATUS, DATA }),
        }
      );

      if (!response.ok) throw new Error("Erro ao atualizar o pedido");

      message.success(`Pedido ${ID_PEDIDO} atualizado com sucesso!`, 1); // 1 segundo
      return true;
    } catch (error) {
      message.error("Erro ao atualizar o pedido.",1);
      return false;
    }
  };

  const aceitarPedido = async (ID_PEDIDO) => {
    if (loadingPedido === ID_PEDIDO) return;
    setLoadingPedido(ID_PEDIDO);

    const pedidoAtual = pedidosNovos.find(
      (p) => p.ID_PEDIDO === ID_PEDIDO
    );
    if (!pedidoAtual) {
      message.error("Pedido não encontrado.",1);
      setLoadingPedido(null);
      return;
    }

    const novoStatus = Number(pedidoAtual.STATUS) + 1;
    const DATA = new Date().toISOString();
    const sucesso = await alterarStatusPedido(ID_PEDIDO, novoStatus, DATA);

    if (sucesso) await fetchPedidosNovos();
    setLoadingPedido(null);
  };

  const finalizarPedido = (ID_PEDIDO) => {
    confirm({
      title: "Finalizar Pedido",
      icon: <CheckCircleOutlined style={{ color: "green" }} />,
      content:
        "Tem certeza que deseja avançar este pedido para o fim da produção?",
      okText: "Sim, finalizar",
      okType: "primary",
      cancelText: "Cancelar",
      onOk: async () => {
        setLoadingPedido(ID_PEDIDO);
        const DATA = new Date().toISOString();
        const sucesso = await alterarStatusPedido(ID_PEDIDO, 6, DATA);
        if (sucesso) await fetchPedidosNovos();
        setLoadingPedido(null);
      },
    });
  };

  return (
    <Col xs={24} sm={24} md={24}>
      <div style={{ padding: "10px" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#1890ff",
            color: "white",
            padding: "10px 15px",
            borderRadius: "8px",
            marginBottom: "15px",
            fontWeight: "bold",
            fontSize: "16px",
            position: "relative",
          }}
        >
          <span>Em Análise</span>

          <span
            style={{
              position: "absolute",
              top: "27%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <Tooltip
              title={
                somHabilitado
                  ? "Som habilitado"
                  : "Clique para habilitar o som"
              }
            >
              <Button
                shape="circle"
                size="large"
                icon={
                  somHabilitado ? (
                    <FaVolumeUp color="green" />
                  ) : (
                    <FaVolumeMute color="red" />
                  )
                }
                onClick={() => {
                  if (!somHabilitado) {
                    audioRef.current = new Audio("/sons/alarme.mp3");
                    audioRef.current.loop = true;
                    audioRef.current
                      .play()
                      .catch((err) =>
                        console.warn("Erro ao tocar áudio:", err)
                      );
                    setSomHabilitado(true);
                  } else {
                    pararAlarme();
                    setSomHabilitado(false);
                  }
                }}
              />
            </Tooltip>
          </span>

          <span>{pedidosNovos.length} Pedido(s)</span>
        </div>

        {/* Lista de pedidos */}
        {pedidosNovos.length > 0 ? (
          <List
            itemLayout="horizontal"
            dataSource={pedidosNovos}
            renderItem={(pedido) => (
              <React.Fragment key={pedido.ID_PEDIDO}>
                <List.Item
                  style={{ padding: "10px 0" }}
                  actions={[
                    <Space>
                      <Button
                        key="aceitar"
                        type="primary"
                        size="small"
                        loading={loadingPedido === pedido.ID_PEDIDO}
                        onClick={() => aceitarPedido(pedido.ID_PEDIDO)}
                      >
                        <ArrowRightOutlined />
                      </Button>
                      <Button
                        key="finalizar"
                        type="primary"
                        size="small"
                        style={{
                          backgroundColor: "green",
                          borderColor: "green",
                        }}
                        loading={loadingPedido === pedido.ID_PEDIDO}
                        onClick={() => finalizarPedido(pedido.ID_PEDIDO)}
                      >
                        Finalizar
                      </Button>
                    </Space>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <span style={{ fontWeight: "bold" }}>
                        Pedido {pedido.ID_PEDIDO} - {pedido.TOTAL}
                      </span>
                    }
                    description={
                      <div style={{ fontSize: "14px" }}>
                        <div>
                          <strong>Usuário:</strong>{" "}
                          {usuarios[pedido.ID_USUARIO] ||
                            "Não informado"}
                        </div>
                        <div>
                          <strong>Pagamento:</strong> {pedido.PAGAMENTO}
                        </div>
                        <div>
                          <strong>Data:</strong> {pedido.DATA}
                        </div>
                      </div>
                    }
                  />
                </List.Item>
                <Divider style={{ margin: "8px 0" }} />
              </React.Fragment>
            )}
          />
        ) : (
          <p style={{ textAlign: "center", marginTop: "20px" }}>
            Nenhum pedido
          </p>
        )}
      </div>
    </Col>
  );
};

export default PedidosNovos;
